import { httpClient } from "@/app/client/httpClient";
import { authStore } from "@/store/auth";
import { masterStore } from "@/store/master";
import { orderStore } from "@/store/order";
import { CAREER_SEMINAR_OFFER_TYPE_SLUG } from "@/types/Master";
import { PageableRequest } from "@/types/PageableRequest";
import { Application } from "@/types/order/Application";
import { OrderDetail, SlimOrder } from "@/types/order/Order";
import { OrderStatus } from "@/types/order/OrderStatus";
import { OrderWorkTimeOption } from "@/types/order/OrderWorkTimeOption";
import {
  addMinutes,
  formatFromISOString,
  formatISO,
  getWorkingTimeText,
  now,
  parseISO
} from "@/utils/date";

class OrderService {
  searchOrders(
    query?: Partial<{
      current_latitude: number;
      current_longitude: number;
      date__in: string;
      locality__in: string;
      office__prefecture__in: string;
      qualification__in: string;
      service_name: string;
      service_type__in: string;
      shift_type__in: string;
      duration_type__in: string;
      only_hiring: boolean;
      only_nursing: string;
      exclude_career: boolean;
      begin_hour: number;
      end_hour: number;
      free_word: string;
      hourly_wage: number;
      recommendation_points__in: string;
      without_bath_assistance: boolean;
      with_shuttle_driver_duties: boolean;
      can_commute_by_car: boolean;
      service_started_within_last_five_years: boolean;
      sort_key: string;
      limit: number;
      offset: number;
      user_id: string;
    }>
  ) {
    const queryParameter = {
      query: {
        current_latitude: query?.current_latitude,
        current_longitude: query?.current_longitude,
        date__in: query?.date__in,
        locality__in: query?.locality__in,
        office__prefecture__in: query?.office__prefecture__in,
        qualification__in: query?.qualification__in,
        service_name: query?.service_name,
        service_type__in: query?.service_type__in,
        shift_type__in: query?.shift_type__in,
        duration_type__in: query?.duration_type__in,
        only_hiring: query?.only_hiring,
        only_nursing: query?.only_nursing,
        exclude_career: query?.exclude_career,
        begin_hour: query?.begin_hour,
        end_hour: query?.end_hour,
        free_word: query?.free_word,
        hourly_wage: query?.hourly_wage,
        recommendation_points__in: query?.recommendation_points__in,
        without_bath_assistance: query?.without_bath_assistance,
        with_shuttle_driver_duties: query?.with_shuttle_driver_duties,
        can_commute_by_car: query?.can_commute_by_car,
        service_started_within_last_five_years:
          query?.service_started_within_last_five_years,
        sort_key: query?.sort_key,
        limit: query?.limit || 99999,
        offset: query?.offset || 0,
        user_id: query?.user_id
      }
    };
    if (authStore.existToken) {
      return httpClient.orders.search.$get(queryParameter);
    }
    return httpClient.orders.public_orders.search.$get(queryParameter);
  }

  fetchOrder(
    id: number,
    ignoreWorkerService = false,
    shareKey?: string,
    transitionSource?: string
  ) {
    return httpClient.orders
      ._id(id)
      .$get({ query: { ignoreWorkerService, shareKey, transitionSource } });
  }

  fetchTotalAverageEvaluation(userId: string) {
    return httpClient.orders.total_avg_evaluations_by_office.$get({
      query: { user: userId }
    });
  }

  updateOrderStatus(orderId: number, status: OrderStatus) {
    return httpClient.orders._id(orderId).$patch({
      body: {
        status
      }
    });
  }

  fetchWithholdingNendoList() {
    return httpClient.orders.withholding.nendo_list.$get();
  }

  fetchWithholdings(query?: { nendo?: number; corporation?: number }) {
    return httpClient.orders.withholding.$get({
      query: {
        nendo: query?.nendo,
        corporation: query?.corporation
      }
    });
  }

  fetchTotalEvaluationByOffice(userId: string) {
    return httpClient.orders.total_avg_evaluations_by_office.$get({
      query: {
        user: userId
      }
    });
  }

  createPublishWithholding(body: {
    startJobDate: string;
    companyName: string;
    full_name: string;
  }) {
    return httpClient.orders.publish_withholding_tax_slip_request.$post({
      body
    });
  }

  fetchEvaluationByOffice(
    query: PageableRequest<{
      application: number;
      application__worker: string;
      application__order__office: string;
      ordering: string;
    }>
  ) {
    return httpClient.orders.evaluation_by_office.$get({ query });
  }

  evaluationByOffice(body: {
    application: number;
    scorePunctuality: number;
    scoreGreeting: number;
    scorePerformance: number;
    scoreCommunication: number;
    scoreSkill: number;
    thankYouMessage: string;
  }) {
    return httpClient.orders.evaluation_by_office.$post({ body });
  }

  evaluationByWorker(body: {
    application: number;
    scoreScheduling: number;
    scoreCorrectness: number;
    scoreInstructions: number;
    scoreGreeting: number;
    scoreCleanliness: number;
    goodPoint: string;
    badPoint: string;
    desireToWork?: number;
  }) {
    return httpClient.orders.evaluation_by_worker.$post({ body });
  }

  registerDesireToWork(evaluationByWorkerId: number, desireToWork: number) {
    return httpClient.orders.evaluation_by_worker
      ._id(evaluationByWorkerId)
      .desire_to_work.$patch({
        body: { desireToWork }
      });
  }

  getNotFixedNoticeOfEmploymentPdfUrl(
    orderId: number,
    workTimeOptionId: number
  ) {
    return httpClient.orders.notice_of_employment_url.not_fixed.$post({
      body: {
        orderId: orderId,
        workTimeOptionId: workTimeOptionId
      }
    });
  }

  getFixedNoticeOfEmploymentPdfUrl(applicationId: number) {
    return httpClient.orders.notice_of_employment_url
      ._id(applicationId)
      .$post();
  }

  getNoticeOfEmploymentArchiveList(applicationId: number) {
    return httpClient.orders.notice_of_employment_archive_list
      ._id(applicationId)
      .$get();
  }

  getArchivedNoticeOfEmploymentPdfUrl(
    applicationId: number,
    archiveId: number
  ) {
    return httpClient.orders.notice_of_employment_url
      ._id(applicationId)
      .archive._id(archiveId)
      .$post();
  }

  async fetchMyApplicationFromOrderId(
    orderId: number
  ): Promise<Application | null> {
    return await httpClient.orders._id(orderId).application.$get();
  }

  async calcSalary(
    hourlyWage: number,
    variousAllowances: number,
    startWorkAt: number,
    finishWorkAt: number,
    startRestAt: number | null,
    finishRestAt: number | null,
    actualStartWorkAt: number | null,
    actualFinishWorkAt: number | null,
    actualStartRestAt: number | null,
    actualFinishRestAt: number | null
  ) {
    return await httpClient.orders.calc_salary.$post({
      body: {
        hourlyWage,
        variousAllowances,
        startWorkAt: formatISO(startWorkAt),
        finishWorkAt: formatISO(finishWorkAt),
        startRestAt: startRestAt ? formatISO(startRestAt) : undefined,
        finishRestAt: finishRestAt ? formatISO(finishRestAt) : undefined,
        actualStartWorkAt: actualStartWorkAt
          ? formatISO(actualStartWorkAt)
          : undefined,
        actualFinishWorkAt: actualFinishWorkAt
          ? formatISO(actualFinishWorkAt)
          : undefined,
        actualStartRestAt: actualStartRestAt
          ? formatISO(actualStartRestAt)
          : undefined,
        actualFinishRestAt: actualFinishRestAt
          ? formatISO(actualFinishRestAt)
          : undefined
      }
    });
  }

  fetchWorkSession({
    hourlyWage,
    variousAllowances,
    startWorkAt,
    finishWorkAt,
    startRestAt,
    finishRestAt
  }: {
    hourlyWage: number;
    variousAllowances: number;
    startWorkAt: string;
    finishWorkAt: string;
    startRestAt: string | null;
    finishRestAt: string | null;
  }) {
    return httpClient.orders.work_session.$get({
      query: {
        hourly_wage: hourlyWage,
        various_allowances: variousAllowances,
        start_work_at: startWorkAt,
        finish_work_at: finishWorkAt,
        start_rest_at: startRestAt || undefined,
        finish_rest_at: finishRestAt || undefined
      }
    });
  }

  createAttendanceCorrection(body: {
    application: number;
    actualStartedWorkAt: string;
    actualFinishedWorkAt: string;
    actualStartedRestAt: string | null;
    actualFinishedRestAt: string | null;
    workerComment: string;
  }) {
    return httpClient.orders.attendance_corrections.create.$post({ body });
  }
  updateAttendanceCorrection(
    attendanceCorrectionId: number,
    body: {
      actualStartedWorkAt: string;
      actualFinishedWorkAt: string;
      actualStartedRestAt: string | null;
      actualFinishedRestAt: string | null;
      workerComment: string;
    }
  ) {
    return httpClient.orders.attendance_corrections
      ._id(attendanceCorrectionId)
      .submit.$post({ body });
  }

  fetchAnonymousEvaluations(officeId: number) {
    return httpClient.orders.anonymous_evaluations._officeId(officeId).$get();
  }

  countUp({
    office,
    serviceName,
    onlyHiring
  }: {
    office: number;
    serviceName: string;
    onlyHiring: boolean;
  }) {
    /* eslint-disable @typescript-eslint/naming-convention */
    const queryParameter = {
      query: {
        office: office,
        service_name: serviceName,
        only_hiring: onlyHiring
      }
    };
    /* eslint-enable */
    return httpClient.orders.count_up.$get(queryParameter);
  }

  fetchOrdersEachDay(office: number, serviceName: string) {
    /* eslint-disable @typescript-eslint/naming-convention */
    const queryParameter = {
      query: {
        office: office,
        service_name: serviceName
      }
    };
    /* eslint-enable */
    return httpClient.orders.one_order_each_day.$get(queryParameter);
  }

  async fetchOrdersEachDayWithCount(body: {
    office: number;
    serviceName: string;
    onlyHiring: boolean;
  }) {
    const [countResult, ordersResult] = await Promise.all([
      this.countUp(body),
      this.fetchOrdersEachDay(body.office, body.serviceName)
    ]);
    return {
      count: countResult.count,
      results: ordersResult
    };
  }

  // Orderに関する処理を行うメソッド軍
  /**
   * 勤務日を下記の形式で返す
   * 2023年08月01日(月)
   */
  getFormattedDateWithYear(order: OrderDetail | SlimOrder) {
    const v = order.date;
    return formatFromISOString(v, "yyyy年MM月dd日(iiiii)");
  }

  /**
   * 勤務日を下記の形式で返す
   * 08/01(月)
   */
  getFormattedDateWithSlash(order: OrderDetail | SlimOrder) {
    const v = order.date;
    return formatFromISOString(v, "MM/dd(iiiii)");
  }

  /**
   * 勤務時間を下記の形式で返す
   * 08:00-17:00
   */
  getFormattedWorkTimeRange(order: OrderDetail): string {
    const option = order.workTimeOptions?.[0];
    if (!option) return "";
    return this.getFormattedWorkTimeRangeFromDate(
      option.startWorkAt,
      option.finishWorkAt
    );
  }

  /**
   * 勤務時間を下記の形式で返す
   * 08:00-17:00
   */
  getFormattedWorkTimeRangeFromWorkTimeOption(
    option: OrderWorkTimeOption
  ): string {
    if (!option) return "";
    return this.getFormattedWorkTimeRangeFromDate(
      option.startWorkAt,
      option.finishWorkAt
    );
  }

  /**
   * 勤務時間を下記の形式で返す
   * 08:00-17:00
   */
  getFormattedWorkTimeRangeFromDate(
    startWorkAt: string,
    finishWorkAt: string
  ): string {
    return `${formatFromISOString(startWorkAt, "HH:mm")}-${formatFromISOString(
      finishWorkAt,
      "HH:mm"
    )}`;
  }

  /**
   * 開始日付と終了日付が同じ場合には
   * 23/03/30 20:00 - 05:00
   * と返し、異なる場合には下記のように返す
   * 23/03/30 20:00 - 23/03/31 05:00
   */
  getWorkingTimeText(o: OrderWorkTimeOption): string {
    if (!o) return "";
    return getWorkingTimeText(o.startWorkAt, o.finishWorkAt);
  }

  /** SalaryPerDayとvariousAllowancesを合計して返す */
  getSalaryPerDayWithVariousAllowances(order: OrderDetail): number {
    const option = order.workTimeOptions[0];
    if (!option) return 0;
    return this.getSalaryPerDayWithVariousAllowancesSpecifiedWorkTimeOption(
      order,
      option
    );
  }

  /** SalaryPerDayとvariousAllowancesを合計して返す */
  getSalaryPerDayWithVariousAllowancesFromSlimOrder(
    order: SlimOrder,
    option: OrderWorkTimeOption
  ): number {
    if (!option) return 0;
    return option.salaryPerDay + order.variousAllowances;
  }

  /** SalaryPerDayとvariousAllowancesを合計して返す */
  getSalaryPerDayWithVariousAllowancesSpecifiedWorkTimeOption(
    order: OrderDetail,
    option: OrderWorkTimeOption
  ): number {
    if (!option) return 0;
    const salaryPerDay = option.salaryPerDay;
    const variousAllowances = order.variousAllowances;
    return salaryPerDay + variousAllowances;
  }

  /** SalaryPerDayとvariousAllowancesを合計して返す */
  getAppliedSalaryPerDayWithVariousAllowances(
    application: Application
  ): number {
    const salaryPerDay = application.appliedSalaryPerDay;
    const variousAllowances = application.variousAllowances;
    return salaryPerDay + variousAllowances;
  }

  getWorkTimeRangeWithRestTimeFromWorkTimeOption(
    option: OrderWorkTimeOption
  ): string {
    if (!option) return "";
    return this.getWorkTimeRangeWithRestTimeFromDate(
      option.startWorkAt!,
      option.finishWorkAt!,
      option.hasRest,
      option.hasRest ? option.startRestAt : null,
      option.hasRest ? option.finishRestAt : null
    );
  }
  /**
   * 1件目の勤務時間を下記の形式で返す
   * 08:00 - 17:00
   */
  getWorkTimeRangeFromSlimOrder(option: OrderWorkTimeOption): string {
    if (!option) return "";
    const startWorkTime = option.startWorkAt
      ? formatFromISOString(option.startWorkAt, "HH:mm")
      : "";
    const finishWorkTime = option.finishWorkAt
      ? formatFromISOString(option.finishWorkAt, "HH:mm")
      : "";
    return `${startWorkTime} ~ ${finishWorkTime} `;
  }
  /**
   * 下記のような形式で勤務時間を返す
   * 21:00 ~ 05:00 (休憩なし)
   * 21:00 ~ 05:00 (60分)
   */
  getWorkTimeRangeWithRestTimeFromDate(
    startWorkAt: string,
    finishWorkAt: string,
    hasRest: boolean,
    startRestAt: string | null,
    finishRestAt: string | null
  ): string {
    const startWorkTime = startWorkAt
      ? formatFromISOString(startWorkAt, "HH:mm")
      : "";
    const finishWorkTime = finishWorkAt
      ? formatFromISOString(finishWorkAt, "HH:mm")
      : "";
    let restMinutes = 0;
    if (hasRest) {
      restMinutes = Math.floor(
        (parseISO(finishRestAt!) - parseISO(startRestAt!)) / 1000 / 60
      );
    }
    const restLabel =
      restMinutes === 0 || !restMinutes ? "休憩なし" : `休憩: ${restMinutes}分`;
    return `${startWorkTime} ~ ${finishWorkTime} (${restLabel})`;
  }

  getNotExpiredWorkTimeOptions(
    workTimeOptions: OrderWorkTimeOption[]
  ): OrderWorkTimeOption[] {
    const currentTime = now();
    return workTimeOptions.filter(o => currentTime < parseISO(o.visibleTo));
  }

  /**
   * 限定公開案件かどうかを返す
   *
   * workTimeOptionsやautoOpenOffsetを考慮している
   */
  isLimited(order: SlimOrder): boolean {
    // 限定公開案件でない場合
    if (
      !order.isLimited &&
      !order.isCorporationLimited &&
      !order.isUrlLimited
    ) {
      return false;
    }
    // 自動公開指定が無い場合
    if (!order.isAutoOpened) {
      return true;
    }

    const date = now();
    // NOTE: 限定公開対象ワーカーに対しては、自動公開されたオプションのみがレスポンスに含まれる
    // 上記から、自動公開されていないオプションがある＝限定公開中とみなす
    return order.workTimeOptions.some(option => {
      if (!option) return false;
      const autoOpenedAt = addMinutes(
        parseISO(option.startWorkAt),
        order.autoOpenOffset
      );
      return autoOpenedAt > date;
    });
  }

  async fetchLimitedOrders(fetchType: "append" | "reloadIfCacheExpired") {
    const FETCH_LIMIT = 50;

    // 追加読み込みではなく、かつキャッシュが存在する場合は何もしない
    const limitedOrderListCache = orderStore.limitedOrderListCache;
    const isCacheExpired = orderStore.limitedOrderListCacheExpiredAt <= now();
    if (
      fetchType === "reloadIfCacheExpired" &&
      limitedOrderListCache !== undefined &&
      !isCacheExpired
    ) {
      return orderStore.limitedOrderListCache ?? [];
    }

    if (fetchType === "reloadIfCacheExpired") {
      //  再読み込みの場合は、キャッシュをクリアする
      // 追加読み込みの場合は、既にキャッシュされた案件に追加する分をAPIから取得するためキャッシュは削除しない
      orderStore.clearLimitedOrderListCache();
    }

    const orders = await httpClient.orders.search_limited.$get({
      query: {
        limit: FETCH_LIMIT,
        offset: (orderStore.showPageOfLimitedOrderList - 1) * FETCH_LIMIT
      }
    });
    if (
      orders.length === 0 &&
      // 既に orderStore が存在しているワーカーの場合、ストアが初期化されず limitedOrderList = undefined の場合がある
      // そのため undefined の場合には `|| []` で空リストを対象に .length へアクセスする
      (orderStore.limitedOrderListCache || []).length > 0
    ) {
      orderStore.setIsAllLimitedOrderLoaded(true);
      return orderStore.limitedOrderListCache ?? [];
    }
    const result = [...(orderStore.limitedOrderListCache ?? []), ...orders];
    orderStore.addLimitedOrderListCache([...orders]);
    orderStore.setShowPageOfLimitedOrderList(
      orderStore.showPageOfLimitedOrderList + 1
    );

    return result;
  }

  fetchIndividualOfferOrders(limit: number, offset: number) {
    return httpClient.orders.search_individual_offer.$get({
      query: { limit, offset }
    });
  }

  async fetchCountOfLimitedOrders() {
    // 有効なキャッシュがあればキャッシュから取得、なければAPIから取得
    if (orderStore.limitedOrderListCountCacheExpiredAt < now()) {
      const { count } = await httpClient.orders.count_up_limited_orders.$get();
      orderStore.setLimitedOrderListCountCache(count);
      return { count };
    }
    return { count: orderStore.limitedOrderListCountCache };
  }

  fetchCountOfIndividualOfferOrders() {
    return httpClient.orders.count_up_individual_offer_orders.$get();
  }

  fetchOrdersByOrderIds(query: {
    order__in: string;
    limit?: number;
    offset?: number;
  }) {
    return httpClient.orders.search_orders_by_order_ids.$get({
      query
    });
  }

  searchOrdersByOfficeIds(query: {
    office__in: string;
    date__gte: string;
    date__lte?: string;
    service_name?: string;
    include_closed?: boolean;
  }) {
    return httpClient.orders.search_orders_by_office_ids.$get({
      query: query
    });
  }

  isWatchLaterButtonVisible(order: OrderDetail | SlimOrder | null) {
    if (order === null) {
      return false;
    }

    return order.isHiring;
  }

  isCareerOrder(order: OrderDetail | SlimOrder) {
    return (
      masterStore.getOfferTypeSlugById(order.offerType) ===
      CAREER_SEMINAR_OFFER_TYPE_SLUG
    );
  }
}

export const orderService = new OrderService();
